import React from 'react';
import { View, ScrollView, TouchableOpacity, Alert, TextInput, ActivityIndicator, Modal, KeyboardAvoidingView, Platform } from 'react-native';
import { Text } from '@/components';
import { Share } from 'react-native';
import { useSafeAreaInsets, useSafeAreaFrame } from 'react-native-safe-area-context';
import { ArrowLeft, Check, ChevronRight, Plus, Pencil, X } from 'lucide-react-native';
import { useRouter, useNavigation } from 'expo-router';
import accountStyles from './account.styles';
import settingsStyles from '../settings.styles';
import theme from '@/constants/colors';
import { useAuth } from '@/context/AuthContext';
import { useUserProfile, useUpdateAccount, useSetTailTag } from '@/lib/queries/account';
import { useInviteSuggestions } from '@/lib/queries/groups';
import { useMyGroups, useGroupInvites, useCreateInvite, useRevokeInvite } from '@/lib/queries/groups';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { API_URL } from '@/lib/api';
import * as Haptics from 'expo-haptics';
import { openAppSettings } from '@/utils/permissions';
import { logEvent } from '@/lib/analytics';
import { ensureJpegImage } from '@/utils/imageConversion';

const Icon = require('../../../assets/images/icon.png');

export default function AccountSettingsScreen() {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { t } = useTranslation();
  const auth = useAuth();
  const { user, session } = auth || {};
  const { data: userProfile } = useUserProfile(session?.accessToken);
  const updateAccountMutation = useUpdateAccount(session?.accessToken);
  const { currentLanguage, setLanguage, supportedLanguages } = useLanguage();

  const [editedName, setEditedName] = React.useState<string>('');
  const [editedPhoto, setEditedPhoto] = React.useState<string | number>('');
  const [isSaving, setIsSaving] = React.useState(false);

  // ─── Family section (design: User Profile Settings.svg) ───
  const token = session?.accessToken;
  const groupsQuery = useMyGroups(token, 'family');
  const familyGroup = (groupsQuery.data ?? []).find((g) => g.type === 'family') ?? null;
  const invitesQuery = useGroupInvites(token, familyGroup?.id);
  const pendingInvites = (invitesQuery.data ?? []).filter((i) => !i.acceptedAt);
  const createInviteMutation = useCreateInvite(token);
  const setTailTagMutation = useSetTailTag(token);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const suggestions = useInviteSuggestions(token, familyGroup?.id, inviteEmail);
  const navigation = useNavigation();
  React.useEffect(() => {
    // full-screen settings page: hide the tab bar (same pattern as the pack screens)
    (navigation as any).setOptions?.({ tabBarStyle: { display: 'none' } });
    return () => (navigation as any).setOptions?.({ tabBarStyle: { display: 'flex' } });
  }, [navigation]);
  const revokeInviteMutation = useRevokeInvite(token);

  const [tagEditing, setTagEditing] = React.useState(false);
  const [editedTag, setEditedTag] = React.useState('');
  const tailTag = userProfile?.tailTag ?? '';

  const handleInvite = async (targetOverride?: string) => {
    const target = (targetOverride ?? inviteEmail).trim();
    if (!familyGroup || !target) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    try {
      await createInviteMutation.mutateAsync({ groupId: familyGroup.id, email: target });
      setInviteEmail('');
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (e: any) {
      Alert.alert(t('common.error'), e?.message || t('groups.inviteFailed'));
    }
  };


  const saveTailTag = async () => {
    const tag = editedTag.trim().replace(/^@+/, '').toLowerCase();
    if (!tag) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    try {
      await setTailTagMutation.mutateAsync(tag);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (e: any) {
      Alert.alert(t('common.error'), e?.message || t('account.tailTagSaveFailed'));
    }
  };

  React.useEffect(() => {
    const defaultName = userProfile?.name || user?.email?.split('@')[0] || '';
    const defaultPhoto = userProfile?.image || Icon;
    setEditedName(defaultName);
    setEditedPhoto(defaultPhoto);
  }, [user, userProfile]);

  const handlePickImage = async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const { status: existingStatus } = await ImagePicker.getMediaLibraryPermissionsAsync();

    if (existingStatus === 'denied') {
      Alert.alert(
        t('account.photoLibraryAccessNeeded'),
        t('account.photoLibraryReason'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('common.openSettings'), onPress: openAppSettings },
        ],
      );
      return;
    }

    if (existingStatus !== 'granted') {
      await new Promise<void>((resolve) => {
        Alert.alert(
          t('account.allowPhotoLibrary'),
          t('account.allowPhotoLibraryReason'),
          [{ text: t('common.continue'), onPress: () => resolve() }],
          { cancelable: false },
        );
      });

      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          t('account.photoLibraryAccessNeeded'),
          t('account.photoLibraryNeeded'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('common.openSettings'), onPress: openAppSettings },
          ],
        );
        return;
      }
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setEditedPhoto(result.assets[0].uri);
    }
  };

  const handleSave = async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    if (!editedName.trim()) {
      Alert.alert(t('common.error'), t('account.nameCannotBeEmpty'));
      return;
    }
    setIsSaving(true);
    try {
      let finalPhoto: string | undefined;

      if (typeof editedPhoto === 'string' && editedPhoto && !editedPhoto.startsWith('http')) {
        if (!session?.accessToken || !user?.id) throw new Error('No active session for upload');

        const convertedUri = await ensureJpegImage(editedPhoto);
        const form = new FormData();
        const uriParts = convertedUri.split('/');
        const rawName = uriParts[uriParts.length - 1] || `photo_${Date.now()}.jpg`;
        const fileName = rawName.split(/[?#]/)[0];
        const extMatch = fileName.match(/\.([a-zA-Z0-9]+)$/);
        const ext = extMatch ? extMatch[1].toLowerCase() : 'jpg';
        const mimeType = ext === 'png' ? 'image/png' : 'image/jpeg';
        // @ts-ignore
        form.append('file', { uri: convertedUri, name: fileName, type: mimeType });
        form.append('filename', fileName);
        form.append('dog_id', '');
        form.append('trail_id', '');
        form.append('user_id', user.id);

        const uploadResp = await fetch(`${API_URL}/uploads`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.accessToken}`,
            accept: 'application/json',
          },
          body: form as any,
        });

        if (!uploadResp.ok) {
          const errText = await uploadResp.text();
          throw new Error(`Upload failed: ${uploadResp.status} ${errText}`);
        }

        const uploadData = await uploadResp.json();
        finalPhoto = uploadData.public_url || uploadData.publicUrl || uploadData.url;
        if (!finalPhoto) throw new Error('Upload did not return a public URL');
      } else if (typeof editedPhoto === 'string' && editedPhoto.startsWith('http')) {
        finalPhoto = editedPhoto;
      }

      // tail tag: save first so a conflict (409) aborts before the profile update
      const newTag = editedTag.trim().replace(/^@+/, '').toLowerCase();
      if (tagEditing && newTag && newTag !== tailTag) {
        await setTailTagMutation.mutateAsync(newTag);
      }

      await updateAccountMutation.mutateAsync({ name: editedName.trim(), image: finalPhoto });
      logEvent('profile_updated', { section: 'account' });
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      Alert.alert(t('account.profileUpdated'), t('account.profileUpdatedMessage'));
      router.push('/settings');
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      Alert.alert(t('common.error'), error?.message || t('account.failedToUpdateProfile'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectLanguage = async (code: string) => {
    if (code === currentLanguage) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    logEvent('language_changed', { from: currentLanguage, to: code });
    await setLanguage(code);
  };

  return (
    <View style={[accountStyles.container, { paddingTop: insets.top }]}>
      <View style={accountStyles.header}>
        <TouchableOpacity style={accountStyles.backButton} onPress={() => router.push('/settings')}>
          <ArrowLeft size={20} color={theme.accentPrimary} />
        </TouchableOpacity>
        <Text style={accountStyles.title}>{t('account.title')}</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={accountStyles.modalContent}>
        <View style={accountStyles.photoSection}>
          <TouchableOpacity style={accountStyles.photoContainer} onPress={handlePickImage}>
            {editedPhoto ? (
              <Image source={editedPhoto} style={accountStyles.editPhoto} contentFit="cover" />
            ) : (
              <View style={[accountStyles.editPhoto, accountStyles.initialAvatar]}>
                <Text style={accountStyles.initialAvatarText}>{(editedName || userProfile?.name || user?.email || 'A').charAt(0).toUpperCase()}</Text>
              </View>
            )}
          </TouchableOpacity>
          <Text style={accountStyles.photoHint}>{t('account.tapToChangePhoto')}</Text>
        </View>

        <View style={accountStyles.inputSection}>
          <Text style={accountStyles.inputLabel}>{t('account.tailTagLabel')}</Text>
          {tagEditing ? (
            <View style={[accountStyles.disabledInput, { flexDirection: 'row', alignItems: 'center', gap: 8 }]}>
              <Text style={[accountStyles.disabledInputText, { opacity: 0.6 }]}>@</Text>
              <TextInput
                style={{ flex: 1, color: theme.textPrimary, fontSize: 15 }}
                value={editedTag}
                onChangeText={(v) => setEditedTag(v.toLowerCase().replace(/^@+/, ''))}
                autoCapitalize="none"
                autoCorrect={false}
                autoFocus
              />
              <TouchableOpacity hitSlop={8} onPress={() => { setEditedTag(tailTag); setTagEditing(false); }}>
                <X size={16} color={theme.textMuted} />
              </TouchableOpacity>
            </View>
          ) : (
            <View style={[accountStyles.disabledInput, { flexDirection: 'row', alignItems: 'center', gap: 8 }]}>
              <TouchableOpacity
                style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
                onPress={async () => {
                  try { await Share.share({ message: `@${tailTag}` }); } catch {}
                }}
              >
                <Text style={accountStyles.disabledInputText}>{`@${tailTag}`}</Text>
                <Text style={accountStyles.tailTagCopy}>{t('account.tailTagShare')}</Text>
              </TouchableOpacity>
              <TouchableOpacity hitSlop={8} onPress={() => { setEditedTag(tailTag); setTagEditing(true); }}>
                <Pencil size={16} color={theme.textMuted} />
              </TouchableOpacity>
            </View>
          )}
          {tagEditing
            ? <Text style={accountStyles.inputHint}>{t('account.tailTagSaveHint')}</Text>
            : <Text style={accountStyles.inputHint}>{t('account.tailTagHint')}</Text>}
        </View>

        <View style={accountStyles.inputSection}>
          <Text style={accountStyles.inputLabel}>{t('account.nameLabel')}</Text>
          <TextInput style={accountStyles.input} value={editedName} onChangeText={setEditedName} placeholder={t('account.namePlaceholder')} placeholderTextColor={theme.textMuted} />
        </View>

        <View style={accountStyles.inputSection}>
          <Text style={accountStyles.inputLabel}>{t('account.emailLabel')}</Text>
          <View style={accountStyles.disabledInput}>
            <Text style={accountStyles.disabledInputText}>{user?.email}</Text>
          </View>
          <Text style={accountStyles.inputHint}>{t('account.emailCannotChange')}</Text>
        </View>

        {/* Family card */}
        <View style={accountStyles.familyCard}>
          <Text style={accountStyles.familyTitle}>{t('account.familyTitle')}</Text>
          <Text style={accountStyles.familySubtitle}>{t('account.familySubtitle')}</Text>

          {familyGroup ? (
            <>
              <View style={accountStyles.inviteRow}>
                <TextInput
                  style={accountStyles.inviteInput}
                  value={inviteEmail}
                  onChangeText={setInviteEmail}
                  placeholder={t('account.familyEmailPlaceholder')}
                  placeholderTextColor={theme.textMuted}
                  autoCapitalize="none"
                  keyboardType="email-address"
                />
                <TouchableOpacity
                  style={[accountStyles.inviteButton, createInviteMutation.isPending && accountStyles.inviteButtonDisabled]}
                  onPress={() => handleInvite()}
                  disabled={createInviteMutation.isPending || !inviteEmail.trim()}
                >
                  {createInviteMutation.isPending ? (
                    <ActivityIndicator color={theme.backgroundPrimary} size="small" />
                  ) : (
                    <Text style={accountStyles.inviteButtonText}>{t('account.invite')}</Text>
                  )}
                </TouchableOpacity>
              </View>

              {(suggestions.data ?? []).length > 0 && (
                <View style={accountStyles.suggestionList}>
                  {(suggestions.data ?? []).map((u) => (
                    <TouchableOpacity
                      key={u.id}
                      style={accountStyles.suggestionRow}
                      onPress={() => { setInviteEmail(''); handleInvite(u.email); }}
                    >
                      {u.image ? (
                        <Image source={u.image} style={accountStyles.suggestionAvatar} contentFit="cover" cachePolicy="memory-disk" />
                      ) : (
                        <View style={[accountStyles.suggestionAvatar, accountStyles.suggestionAvatarFallback]}>
                          <Text style={accountStyles.initialAvatarText}>{(u.name || u.email || 'A').charAt(0).toUpperCase()}</Text>
                        </View>
                      )}
                      <Text style={accountStyles.suggestionName}>{u.name || u.email}</Text>
                      <Text style={accountStyles.suggestionTag}>{`@${u.tail_tag}`}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {pendingInvites.length > 0 && (
                <View style={{ marginBottom: 14 }}>
                  <Text style={accountStyles.sectionHeading}>{t('account.pendingInvites')}</Text>
                  {pendingInvites.map((inv) => (
                    <View key={inv.id} style={accountStyles.pendingRow}>
                      <View style={{ flex: 1 }}>
                        <Text style={accountStyles.pendingEmail} numberOfLines={1}>{inv.email}</Text>
                        <Text style={accountStyles.pendingSub}>{t('account.invitationSent')}</Text>
                      </View>
                      <TouchableOpacity
                        style={accountStyles.pendingBadge}
                        onPress={() => revokeInviteMutation.mutate({ groupId: familyGroup.id, inviteId: inv.id })}
                      >
                        <Text style={accountStyles.pendingBadgeText}>{t('account.pending')}</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}

              <Text style={accountStyles.sectionHeading}>{t('account.familyMembers')}</Text>
              {(familyGroup.members ?? []).map((m) => (
                <View key={m.userId} style={accountStyles.memberRow}>
                  {m.image ? (
                    <Image source={{ uri: m.image }} style={accountStyles.memberAvatar} contentFit="cover" cachePolicy="memory-disk" />
                  ) : (
                    <View style={accountStyles.memberAvatar} />
                  )}
                  <View style={accountStyles.memberInfo}>
                    <Text style={accountStyles.memberName}>{m.name || t('profile.notAvailable')}</Text>
                  </View>
                  {m.role === 'owner' && (
                    <View style={accountStyles.ownerBadge}>
                      <Text style={accountStyles.ownerBadgeText}>{t('account.owner')}</Text>
                    </View>
                  )}
                  <ChevronRight size={18} color={theme.textMuted} />
                </View>
              ))}
            </>
          ) : (
            <>
              <Text style={accountStyles.familyEmpty}>{t('account.noFamilyYet')}</Text>
              <TouchableOpacity style={accountStyles.familyCreateBtn} onPress={() => router.push('/group/create' as any)} activeOpacity={0.85}>
                <Plus size={18} color={theme.backgroundPrimary} />
                <Text style={accountStyles.familyCreateLink}>{t('groups.createFamily')}</Text>
              </TouchableOpacity>
            </>
          )}

          <Text style={accountStyles.familyFooter}>{t('account.familyFooter')}</Text>
        </View>

        {/* Language Section */}
        <View style={settingsStyles.sectionCard}>
          <Text style={settingsStyles.sectionTitle}>{t('settings.language')}</Text>
          {supportedLanguages.map((lang) => (
            <TouchableOpacity
              key={lang.code}
              style={settingsStyles.row}
              onPress={() => handleSelectLanguage(lang.code)}
              activeOpacity={0.7}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
                <Text style={settingsStyles.rowText}>{lang.nativeName}</Text>
                {lang.code !== 'en' && (
                  <Text style={{ color: theme.textMuted, fontSize: 14 }}>{lang.englishName}</Text>
                )}
              </View>
              {currentLanguage === lang.code && (
                <Check size={18} color={theme.accentPrimary} />
              )}
            </TouchableOpacity>
          ))}
        </View>

      </ScrollView>

      {/* sticky save footer */}
      <View style={[accountStyles.stickyFooter, { paddingBottom: insets.bottom + 12 }]}>
        <TouchableOpacity style={[accountStyles.saveButton, { flex: 1 }, isSaving && accountStyles.saveButtonDisabled]} onPress={handleSave} disabled={isSaving}>
          {isSaving ? <ActivityIndicator color={theme.backgroundPrimary} /> : <Text style={accountStyles.saveButtonText}>{t('account.saveChanges')}</Text>}
        </TouchableOpacity>
      </View>
    </View>
  );
}